import React from 'react'
import Snackbar, { SnackbarProps } from '@material-ui/core/Snackbar'
import { useSnackbar as useSnackbarDefault } from 'material-ui-snackbar-provider'
import Alert from '@material-ui/lab/Alert'
import Slide from '@material-ui/core/Slide'
import CheckCircleOutlineRounded from '@material-ui/icons/CheckCircleOutlineRounded'
import HighlightOffRounded from '@material-ui/icons/HighlightOffRounded'
import WarningRounded from '@material-ui/icons/WarningRounded'
import { IconButton } from '@material-ui/core'
import CloseRounded from '@material-ui/icons/CloseRounded'

type SnackBarSeverity = 'error' | 'warning' | 'info' | 'success'

type SnackBarCustomProps = {
  /** Propriedade que indica o tipo do snackbar */
  severity: SnackBarSeverity
  /** Propriedade que indica a duração da snackbar */
  duration?: number
  marginBottom?: string
}

export type SnackBarProps = {
  message?: string
  action?: string
  SnackbarProps: Partial<SnackbarProps>
  customParameters?: SnackBarCustomProps
}

/** Componente de botão simples para interação do usuário com funcionalidades do sistema */
export const SnackBar: React.FC<SnackBarProps> = (props) => {
  const severity = props.customParameters?.severity ?? 'info'
  const dataCy = `snackBar${severity.charAt(0).toUpperCase() + severity.slice(1)}`
  const marginBottom = props.customParameters?.marginBottom ?? '0px'
  return (
    <Snackbar
      data-cy={dataCy}
      autoHideDuration={props.customParameters?.duration ?? 6000}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      TransitionComponent={(transitionProps) => <Slide direction="up" {...transitionProps} />}
      {...props.SnackbarProps}
    >
      <Alert
        elevation={6}
        variant="filled"
        severity={severity}
        style={{ marginBottom: marginBottom }}
        iconMapping={{ info: <></>, success: <CheckCircleOutlineRounded />, error: <HighlightOffRounded />, warning: <WarningRounded /> }}
        action={
          <IconButton color="inherit" style={{ padding: '0px' }} onClick={(event) => props.SnackbarProps.onClose?.(event, 'timeout')}>
            <CloseRounded />
          </IconButton>
        }
      >
        <p>{props.message}</p>
      </Alert>
    </Snackbar>
  )
}

export const useSnackbar = () => {
  const snackbar = useSnackbarDefault()
  return React.useMemo(() => {
    const showMessage = (severity: SnackBarSeverity, duration?: number) => (message: string, marginBottom?: string) =>
      snackbar.showMessage(message, undefined, undefined, {
        severity,
        duration,
        marginBottom
      })
    return {
      ...snackbar,
      showInfo: showMessage('info'),
      showWarning: showMessage('warning', 10000),
      showError: showMessage('error'),
      showSuccess: showMessage('success')
    }
  }, [snackbar])
}

export default SnackBar
